import { useNode } from "@craftjs/core";
import React, { useState } from "react";
import {
  UnitInput,
  ToggleInput,
  BoxShadowInput,
  MarginInput,
  Panel,
  Tabs,
  TextInput,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  AlignInput,
  CSSFilters,
  PaddingInput,
  RangeNumberInput,
  TabsV2,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { changeTransition, isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

export const PostFeaturedImageSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    width = "100%",
    widthTablet,
    widthMobile,
    height,
    heightTablet,
    heightMobile,
    align,
    alignMobile,
    alignTablet,
    lazyLoad = true,
    altTex,
    titleText,
    boxShadow,
    border,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
    borderRadius,
    cssFilter,
    linkPost,
    openInNewTab,
    hoverBorder,
    hoverBorderRadius,
    hoverBoxShadow,
    transition,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <UnitInput
            resetValue={"100%"}
            value={width}
            isResponsive={true}
            valueTablet={widthTablet}
            valueMobile={widthMobile}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            resetValue={undefined}
            value={height}
            isResponsive={true}
            valueTablet={heightTablet}
            valueMobile={heightMobile}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
            label={"Height"}
          />
          <AlignInput
            resetValue={undefined}
            value={align}
            isResponsive={true}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
          <TextInput
            minLength={INPUT_LIMITS["image-alt-text"].minlength}
            maxLength={INPUT_LIMITS["image-alt-text"].maxlength}
            resetValue={undefined}
            label={"Alternate Text"}
            value={altTex}
            onChange={(val) => {
              setProp((props) => {
                props.altTex = val;
              });
            }}
          />
          <TextInput
            minLength={INPUT_LIMITS["image-title"].minlength}
            maxLength={INPUT_LIMITS["image-title"].maxlength}
            resetValue={undefined}
            label={"Title Text"}
            value={titleText}
            onChange={(val) => {
              setProp((props) => {
                props.titleText = val;
              });
            }}
          />
          <ToggleInput
            resetValue={true}
            label="Lazy load"
            value={lazyLoad}
            onChange={(val) => {
              setProp((props) => {
                props.lazyLoad = val;
              });
            }}
          />
          <ToggleInput
            isResetVisible={false}
            label="Link to Post"
            value={linkPost}
            onChange={(val) => {
              setProp((props) => {
                props.linkPost = val;
              });
            }}
          />
          {linkPost && (
            <>
              <ToggleInput
                resetValue={undefined}
                label="Open in new tab"
                value={openInNewTab}
                onChange={(val) => {
                  setProp((props) => {
                    props.openInNewTab = val;
                  });
                }}
              />
            </>
          )}
        </Spacer>
      </div>
    );
  }

  function AdvancedSettings() {
    const boxShadowTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleBoxShadowStates({
          boxShadowPropKey: "boxShadow",
          boxShadowProp: boxShadow,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleBoxShadowStates({
          boxShadowPropKey: "hoverBoxShadow",
          boxShadowProp: hoverBoxShadow,
        }),
      },
    ];

    function handleBoxShadowStates({ boxShadowPropKey, boxShadowProp }) {
      return (
        <>
          <BoxShadowInput
            resetValue={undefined}
            value={boxShadowProp}
            onChange={(val) => {
              setProp((props) => {
                props[boxShadowPropKey] = val;
              });
            }}
            label={"Box Shadow"}
          />
          {boxShadowPropKey === "hoverBoxShadow" && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.["box-shadow"]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["box-shadow"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    const borderRadiusTabs = [
      {
        label: "Normal",
        key: "normal",
        children: handleBorderRadiusStates({
          borderPropKey: "border",
          borderProp: border,
          borderRadiusPropKey: "borderRadius",
          borderRadiusProp: borderRadius,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleBorderRadiusStates({
          borderPropKey: "hoverBorder",
          borderProp: hoverBorder,
          borderRadiusPropKey: "hoverBorderRadius",
          borderRadiusProp: hoverBorderRadius,
        }),
      },
    ];

    function handleBorderRadiusStates({
      borderPropKey,
      borderProp,
      borderRadiusPropKey,
      borderRadiusProp,
    }) {
      return (
        <>
          <BorderInput
            value={borderProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderPropKey] = val;
              });
            }}
            label={"Border"}
            isResponsive={true}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={borderRadiusProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderRadiusPropKey] = val;
              });
            }}
            label={"Border Radius"}
          />
          {(borderRadiusPropKey === "hoverBorderRadius" ||
            borderPropKey === "hoverBorder") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.[("border", "border-radius")]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["border", "border-radius"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutMargin },
            { key: layoutPadding },
            { key: layoutPaddingTablet },
            { key: layoutPaddingMobile },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <MarginInput
            isResponsive={true}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            resetValue={undefined}
            value={layoutMargin}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            resetValue={undefined}
            value={layoutPadding}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderRadius },
            { key: hoverBorder },
            { key: hoverBorderRadius },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={false}
        >
          <TabsV2 items={borderRadiusTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: boxShadow }])}
          id={`block-setting-${id}-border`}
          title={"Box Shadows"}
          initialOpen={false}
        >
          <TabsV2 items={boxShadowTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: cssFilter }])}
          id={`block-setting-${id}-css-filters`}
          title={"CSS Filters"}
          initialOpen={false}
        >
          <CSSFilters
            resetValue={undefined}
            label="CSS Filters"
            value={cssFilter}
            onChange={(val) => {
              setProp((props) => {
                props.cssFilter = val;
              });
            }}
          ></CSSFilters>
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: width },
        { key: widthTablet },
        { key: widthMobile },
        { key: height },
        { key: heightTablet },
        { key: heightMobile },
        { key: align },
        { key: alignTablet },
        { key: alignMobile },
        { key: altTex },
        { key: titleText },
        { key: lazyLoad },
        { key: linkPost },
        { key: openInNewTab },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutMargin },
        { key: layoutPadding },
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
        { key: border },
        { key: borderRadius },
        { key: cssFilter },
        { key: hoverBorder },
        { key: hoverBorderRadius },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
